/**
 * @deprecated Este módulo (`components/ui/Buttons`) é o conjunto legado de botões.
 *
 * Use `Button` de `@/components/ui/Button` com variant pré-definida.
 * Mantido aqui como wrapper fino sobre o novo `Button` para compat dos consumidores.
 * Refator dos call-sites é follow-up (sub-projeto C-2 — limpeza pós-Task 16).
 *
 * Notas de mapeamento:
 * - `background` prop (legacy) é aceita mas NÃO repassada — o novo `Button` usa variants,
 *   não cor inline. Sites que customizavam cor devem migrar para a variant correta.
 * - `IButtonDivParams` (LogginButton) virou wrapper de `<button>` — funcional, embora a
 *   semântica original fosse `<div>`.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Button as UIButton } from "../Button";
import type { IGoBackButtonParams } from "./types";

type BaseLegacyProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: ReactNode;
  /** @deprecated Legacy — ignorado. Migre para `<Button variant="..." />`. */
  background?: string;
};

function stripLegacy<T extends BaseLegacyProps>(props: T) {
  // Remove props que não fazem sentido no novo Button.
  const { background: _bg, ...rest } = props;
  void _bg;
  return rest;
}

/** @deprecated Use `<Button variant="primary" />` de `@/components/ui/Button`. */
export function PrimaryButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="secondary" />`. */
export function SecondaryButton(props: BaseLegacyProps) {
  return <UIButton variant="secondary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" />` (azul Senne mapeia para primary). */
export function AzulSenneButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="destructive" />`. */
export function DangerButton(props: BaseLegacyProps) {
  return <UIButton variant="destructive" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" />` com ajuste visual via variant futura. */
export function GreenButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="outline" />` (secundário: borda laranja, sem ícone). */
export function GoBackButton({ click }: IGoBackButtonParams) {
  return (
    <UIButton variant="outline" type="button" onClick={click}>
      Voltar
    </UIButton>
  );
}

/** @deprecated Use `<Button variant="outline" />`. */
export function OutlineButton(props: BaseLegacyProps) {
  return <UIButton variant="outline" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" />`. */
export function RegisterButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" />`. */
export function PrimaryResultsButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" size="sm" />`. */
export function TableButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" size="sm" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="ghost" />` ou `<Button variant="outline" />`. */
export function CancelButton(props: BaseLegacyProps) {
  return <UIButton variant="ghost" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" />` (orange Senne mapeia para primary). */
export function OrangeSenneButton(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}

/** @deprecated Use `<Button variant="primary" />`. */
export function LogginButton(props: { children?: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <UIButton variant="primary" {...props} />;
}

/** @deprecated Use `<Button variant="ghost" />` ou variant cinza dedicada. */
export function GrayButton(props: BaseLegacyProps) {
  return <UIButton variant="ghost" {...stripLegacy(props)} />;
}

/** @deprecated Use `Button` de `@/components/ui/Button` diretamente com a variant correta. */
export function Button(props: BaseLegacyProps) {
  return <UIButton variant="primary" {...stripLegacy(props)} />;
}
